import { useMemo, useState } from "react";
import { Eye, Loader2, Plus, Tag, Users } from "lucide-react";
import { toast } from "sonner";
import {
  type CarrierCustomerConnection,
  type CarrierCustomerTier,
  type CarrierLabelDiscountRule,
  useCarrierCustomerTiers,
  useCarrierCustomers,
  useCarrierLabelDiscountRules,
  useCarrierMe,
  useCreateCarrierCustomerTier,
  useDeleteCarrierCustomerTier,
  useUpdateCarrierCustomerTier,
} from "@/api/carrier";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { TablePager, usePagedState } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { BenefitRow } from "@/features/customer-discounts/benefit-row";
import { CustomerDetailDrawer } from "@/features/customer-discounts/customer-detail";
import { CustomersWorkspace } from "@/features/customer-discounts/customers-workspace";
import { PickupBundleBenefit } from "@/features/customer-discounts/pickup-bundle-benefit";
import {
  CustomerOverrideButton,
  LabelDiscountEditor,
} from "@/features/customer-discounts/discount-rule-editors";
import {
  type CustomerDiscountsMode,
  customerSearchText,
  customerStatus,
  customerViewModel,
  DEFAULT_CUSTOMER_TIER_KEY,
} from "@/features/customer-discounts/customer-view-model";
import {
  discountRuleForTier,
  ruleEffectiveStatus,
} from "@/features/customer-discounts/discount-rule-resolution";
import {
  isForbidden,
  mutationFailure,
  useSubmissionGuard,
} from "@/features/customer-discounts/mutation-guards";
import { formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Tier-first customer and discount management.
 *
 * Carriers manage discounts as BENEFITS that live on a customer tier, plus a
 * pinned "Default" tier that holds the all-customers baseline. Pricing resolves
 * customer override › tier benefit › default at quote time (server). The
 * Customers route owns access and tier assignment; Pricing › Discounts owns
 * tier benefits and customer overrides.
 */

const DEFAULT_KEY = DEFAULT_CUSTOMER_TIER_KEY;
type Selected = string;

export default function CustomerDiscountsScreen({
  mode = "discounts",
}: {
  mode?: CustomerDiscountsMode;
}) {
  const me = useCarrierMe();
  const isOwner = me.data?.roles.includes("CARRIER_OWNER") ?? false;
  const { page, pageSize, setPage, setPageSize } = usePagedState();
  const offset = (page - 1) * pageSize;
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerStatus, setCustomerStatus] = useState<
    "ACTIVE" | "BLOCKED" | "REMOVED" | "ALL"
  >("ALL");
  const debouncedCustomerSearch = useDebouncedValue(customerSearch, 300);

  const tiers = useCarrierCustomerTiers();
  const customers = useCarrierCustomers({
    limit: mode === "customers" ? pageSize : 100,
    offset: mode === "customers" ? offset : 0,
    all: mode === "discounts",
    ...(mode === "customers" && {
      q: debouncedCustomerSearch,
      status: customerStatus,
    }),
  });
  const labelRules = useCarrierLabelDiscountRules();

  const tierRows = useMemo(
    () => [...(tiers.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [tiers.data]
  );
  const customerRows = Array.isArray(customers.data)
    ? customers.data
    : (customers.data?.data ?? []);
  const customerPagination = Array.isArray(customers.data)
    ? null
    : (customers.data?.pagination ?? null);
  const rules = labelRules.data ?? [];

  const [selected, setSelected] = useState<Selected>(DEFAULT_KEY);
  const selectedTier =
    selected === DEFAULT_KEY
      ? null
      : (tierRows.find((t) => t.id === selected) ?? null);
  const selectedTierId = selectedTier?.id ?? null;

  const customersInTier = customerRows.filter((row) => {
    return selected === DEFAULT_KEY
      ? row.tierId == null
      : row.tierId === selectedTierId;
  });

  const labelRule = discountRuleForTier(rules, selectedTierId);
  const discountsError = tiers.error ?? customers.error ?? labelRules.error;
  const discountsForbidden = isForbidden(discountsError);
  const discountsLoading =
    tiers.isLoading || customers.isLoading || labelRules.isLoading;
  const hasDiscountData =
    tiers.data !== undefined ||
    customers.data !== undefined ||
    labelRules.data !== undefined;

  if (mode === "customers") {
    return (
      <CustomersWorkspace
        customers={customerRows}
        tiers={tierRows}
        pagination={customerPagination}
        isLoading={customers.isLoading || tiers.isLoading}
        error={customers.error ?? tiers.error}
        isOwner={isOwner}
        search={customerSearch}
        status={customerStatus}
        onSearchChange={(value) => {
          setPage(1);
          setCustomerSearch(value);
        }}
        onStatusChange={(value) => {
          setPage(1);
          setCustomerStatus(value);
        }}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRetry={() => {
          void customers.refetch();
          void tiers.refetch();
        }}
      />
    );
  }

  return (
    <div className="carrier-page">
      <PageHeader
        title="Discounts"
        actions={
          !isOwner ? (
            <span className="rounded-full border px-3 py-1 text-xs font-semibold text-muted-foreground">
              Read-only dispatcher view
            </span>
          ) : undefined
        }
      />

      {/* A precedence rule, kept beside the thing it decides. */}
      <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        Customer overrides beat tier benefits; tier benefits beat the carrier
        default.
      </p>

      {discountsLoading && !hasDiscountData && !discountsError && (
        <Card
          role="status"
          className="flex items-center gap-2 p-4 text-sm text-muted-foreground"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading discounts...
        </Card>
      )}

      {discountsLoading && hasDiscountData && !discountsError && (
        <p
          role="status"
          className="rounded-lg border px-4 py-2 text-xs text-muted-foreground"
        >
          Refreshing discount data...
        </p>
      )}

      {discountsError && (
        <Card
          role="alert"
          className="flex flex-wrap items-center gap-3 p-4 text-sm"
        >
          <span className="text-destructive">
            {discountsForbidden
              ? "You do not have permission to view discounts."
              : hasDiscountData
                ? "Discounts could not refresh. Showing available data."
                : "Failed to load discounts."}
          </span>
          {!discountsForbidden && (
            <Button
              variant="outline"
              size="sm-touch"
              onClick={() => {
                void tiers.refetch();
                void customers.refetch();
                void labelRules.refetch();
              }}
            >
              Retry
            </Button>
          )}
        </Card>
      )}

      {!discountsForbidden &&
        (hasDiscountData || (!discountsLoading && !discountsError)) && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr] lg:gap-5">
            <TierRail
              tiers={tierRows}
              customers={customerRows}
              selected={selected}
              onSelect={setSelected}
              isOwner={isOwner}
            />
            <TierDetail
              tier={selectedTier}
              tierId={selectedTierId}
              isDefault={selected === DEFAULT_KEY}
              isOwner={isOwner}
              customers={customersInTier}
              labelRule={labelRule}
              rules={rules}
              isLoading={tiers.isLoading || customers.isLoading}
              rulesLoading={labelRules.isLoading}
              onDeleted={() => setSelected(DEFAULT_KEY)}
            />
          </div>
        )}
    </div>
  );
}

function TierRail({
  tiers,
  customers,
  selected,
  onSelect,
  isOwner,
}: {
  tiers: CarrierCustomerTier[];
  customers: CarrierCustomerConnection[];
  selected: Selected;
  onSelect: (key: Selected) => void;
  isOwner: boolean;
}) {
  const defaultCount = customers.filter(
    (c) => !c.removed && c.tierId == null
  ).length;

  return (
    <>
      <div className="flex items-center gap-2 lg:hidden">
        <Select value={selected} onValueChange={onSelect}>
          <SelectTrigger
            aria-label="Select customer tier"
            size="sm"
            className="min-w-0 flex-1"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DEFAULT_KEY}>
              Default ({defaultCount})
            </SelectItem>
            {tiers.map((tier) => (
              <SelectItem key={tier.id} value={tier.id}>
                {tier.name} ({tier._count?.connections ?? 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <NewTierButton disabled={!isOwner} />
      </div>

      <Card className="hidden h-fit lg:block">
        <CardHeader className="flex flex-row items-center justify-between p-4 pb-2">
          <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">
            Tiers
          </CardTitle>
          <NewTierButton disabled={!isOwner} />
        </CardHeader>
        <CardContent className="space-y-1 p-3 pt-1">
          <p className="px-1 pb-1 text-[11px] text-muted-foreground">
            entry → premium
          </p>
          <RailItem
            label="Default"
            hint="everyone without a tier"
            count={defaultCount}
            active={selected === DEFAULT_KEY}
            dashed
            onClick={() => onSelect(DEFAULT_KEY)}
          />
          {tiers.map((tier) => (
            <RailItem
              key={tier.id}
              label={tier.name}
              count={tier._count?.connections ?? 0}
              active={selected === tier.id}
              onClick={() => onSelect(tier.id)}
            />
          ))}
          {tiers.length === 0 && (
            <p className="px-2 py-3 text-xs text-muted-foreground">
              No tiers yet. Create one to start giving benefits.
            </p>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function RailItem({
  label,
  hint,
  count,
  active,
  dashed,
  onClick,
}: {
  label: string;
  hint?: string;
  count: number;
  active: boolean;
  dashed?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className={cn(
        "h-auto min-h-10 w-full justify-start gap-3 rounded-lg border px-3 py-2 text-left whitespace-normal transition",
        active ? "border-primary bg-background" : "border-transparent",
        dashed && !active && "border-dashed",
        "hover:bg-muted/50"
      )}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold">{label}</span>
        {hint && (
          <span className="block truncate text-[11px] text-muted-foreground">
            {hint}
          </span>
        )}
      </span>
      <span className="ml-auto text-xs font-semibold text-muted-foreground">
        {count}
      </span>
    </Button>
  );
}

function TierDetail({
  tier,
  tierId,
  isDefault,
  isOwner,
  customers,
  labelRule,
  rules,
  isLoading,
  rulesLoading,
  onDeleted,
}: {
  tier: CarrierCustomerTier | null;
  tierId: string | null;
  isDefault: boolean;
  isOwner: boolean;
  customers: CarrierCustomerConnection[];
  labelRule: CarrierLabelDiscountRule | null;
  rules: CarrierLabelDiscountRule[];
  isLoading: boolean;
  rulesLoading: boolean;
  onDeleted: () => void;
}) {
  const title = isDefault ? "Default" : (tier?.name ?? "Tier");
  const labelPercent = labelRule ? labelRule.percentage : null;
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerStatusFilter, setCustomerStatusFilter] = useState("");
  const [detail, setDetail] = useState<CarrierCustomerConnection | null>(null);
  const { page, pageSize, setPage, setPageSize } = usePagedState();
  const filteredCustomers = useMemo(() => {
    const term = customerSearch.trim().toLowerCase();
    return customers.filter((row) => {
      if (
        customerStatusFilter &&
        customerStatus(row) !== customerStatusFilter
      ) {
        return false;
      }
      if (!term) return true;
      return customerSearchText(row).includes(term);
    });
  }, [customers, customerSearch, customerStatusFilter]);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredCustomers.length / pageSize)
  );
  const currentPage = Math.min(page, totalPages);
  const visibleCustomers = filteredCustomers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const visibleCustomerItems = useMemo(
    () =>
      visibleCustomers.map((row) => ({
        row,
        view: customerViewModel(row, tier ? [tier] : [], rules),
      })),
    [rules, tier, visibleCustomers]
  );

  return (
    <div className="min-w-0 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground">
            {isDefault
              ? "Applies to every customer without a tier."
              : `What every ${title} customer gets, unless overridden.`}
          </p>
        </div>
        {isOwner && !isDefault && tier && (
          <TierActions tier={tier} onDeleted={onDeleted} />
        )}
      </div>

      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base">Benefits</CardTitle>
          <CardDescription>What this tier gives</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 p-4 pt-0">
          <BenefitRow
            icon={<Tag className="h-4 w-4" />}
            title="Label discount"
            description={
              labelRule
                ? `Requested discount · ${labelRule.funding === "CARRIER" ? "Carrier pays" : "EZX pays"}. The applied amount depends on quote funding.`
                : "Choose who pays when configuring this benefit."
            }
            value={
              rulesLoading
                ? "Loading..."
                : labelPercent == null
                  ? "None"
                  : formatPercent(labelPercent)
            }
            status={rulesLoading ? undefined : ruleEffectiveStatus(labelRule)}
            action={
              isOwner && !rulesLoading ? (
                <LabelDiscountEditor
                  tierName={title}
                  tierId={tierId}
                  existing={labelRule}
                />
              ) : undefined
            }
          />
          <PickupBundleBenefit tierId={tierId} isOwner={isOwner} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 p-4 pb-3">
          <CardTitle className="text-base">
            <span className="inline-flex items-center gap-2">
              <Users className="h-4 w-4" />
              Customers {isDefault ? "with no tier" : `in ${title}`} (
              {filteredCustomers.length}/{customers.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex flex-col gap-2 border-y p-3 sm:flex-row sm:items-center sm:justify-between">
            <Input
              type="search"
              aria-label="Search customers"
              className="w-full sm:w-56"
              placeholder="Search customers..."
              value={customerSearch}
              onChange={(event) => {
                setPage(1);
                setCustomerSearch(event.target.value);
              }}
            />
            <Select
              value={customerStatusFilter || "ALL"}
              onValueChange={(value) => {
                setPage(1);
                setCustomerStatusFilter(value === "ALL" ? "" : value);
              }}
            >
              <SelectTrigger
                aria-label="Filter customers by status"
                size="sm"
                className="w-full sm:w-40"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="BLOCKED">Blocked</SelectItem>
                <SelectItem value="LEFT">Left</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isLoading ? (
            <p
              role="status"
              className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> Loading customers...
            </p>
          ) : filteredCustomers.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {customers.length === 0
                ? "No customers here yet."
                : "No customers match your filters."}
            </p>
          ) : (
            <>
              <ul
                aria-label="Discount customer summaries"
                className="divide-y md:hidden"
              >
                {visibleCustomerItems.map(({ row, view }) => {
                  const overrideRule = view.overrideRule;
                  return (
                    <li key={row.id}>
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-auto min-h-11 w-full items-start justify-start gap-3 rounded-none p-3 text-left whitespace-normal hover:bg-muted/50"
                        aria-label={`View discounts for ${view.name}`}
                        onClick={() => setDetail(row)}
                      >
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium">
                                {view.name}
                              </p>
                              <p className="truncate text-xs text-muted-foreground">
                                {view.email}
                              </p>
                            </div>
                            <StatusBadge status={view.status} />
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>Effective: {view.effectiveDiscount}</span>
                            <span>·</span>
                            <span>
                              {overrideRule
                                ? `${formatPercent(overrideRule.percentage)} override`
                                : "Tier default"}
                            </span>
                            {overrideRule && (
                              <StatusBadge
                                status={ruleEffectiveStatus(overrideRule)}
                              />
                            )}
                          </div>
                        </div>
                        <Eye className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                      </Button>
                    </li>
                  );
                })}
              </ul>

              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Effective discount</TableHead>
                      <TableHead>Benefit</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleCustomerItems.map(({ row, view }) => {
                      const overrideRule = view.overrideRule;
                      const status = view.status;
                      const canOverride =
                        isOwner && !row.removed && status === "ACTIVE";
                      return (
                        <TableRow key={row.id}>
                          <TableCell>
                            <span className="font-medium">{view.name}</span>
                            {row.customer.email && (
                              <span className="block text-xs text-muted-foreground">
                                {view.email}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={status} />
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {view.effectiveDiscount}
                          </TableCell>
                          <TableCell>
                            {overrideRule ? (
                              <div className="flex flex-wrap items-center gap-1.5">
                                <Badge variant="secondary">
                                  {formatPercent(overrideRule.percentage)}{" "}
                                  override
                                </Badge>
                                <StatusBadge
                                  status={ruleEffectiveStatus(overrideRule)}
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                Tier default
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setDetail(row)}
                              >
                                View
                              </Button>
                              {canOverride && (
                                <CustomerOverrideButton
                                  customerId={row.customerId}
                                  customerLabel={view.name}
                                  existing={overrideRule}
                                />
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {filteredCustomers.length > 0 && (
                <div className="border-t p-3">
                  <TablePager
                    page={currentPage}
                    pageSize={pageSize}
                    total={filteredCustomers.length}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    disabled={isLoading}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {detail && (
        <CustomerDetailDrawer
          open={true}
          onOpenChange={(open) => {
            if (!open) setDetail(null);
          }}
          row={detail}
          tiers={tier ? [tier] : []}
          isOwner={isOwner}
          mode="discounts"
          rules={rules}
        />
      )}
    </div>
  );
}

function TierActions({
  tier,
  onDeleted,
}: {
  tier: CarrierCustomerTier;
  onDeleted: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [name, setName] = useState(tier.name);
  const updateTier = useUpdateCarrierCustomerTier();
  const deleteTier = useDeleteCarrierCustomerTier();
  const renameSubmission = useSubmissionGuard();
  const deleteSubmission = useSubmissionGuard();
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const assignedCount = tier._count?.connections ?? 0;
  const renamePending = renameSubmission.pending || updateTier.isPending;
  const deletePending = deleteSubmission.pending || deleteTier.isPending;

  async function submitRename() {
    const trimmed = name.trim();
    if (!trimmed) {
      setRenameError("Tier name is required.");
      return;
    }
    if (trimmed === tier.name) {
      setRenaming(false);
      return;
    }
    await renameSubmission.run(async () => {
      setRenameError(null);
      try {
        await updateTier.mutateAsync({ tierId: tier.id, name: trimmed });
        toast.success("Tier renamed.");
        setRenaming(false);
      } catch (error) {
        const message = mutationFailure(error, "rename this tier");
        setRenameError(message);
        toast.error(message);
      }
    });
  }

  async function submitDelete() {
    if (assignedCount > 0) return;
    await deleteSubmission.run(async () => {
      setDeleteError(null);
      try {
        await deleteTier.mutateAsync(tier.id);
        toast.success("Tier deleted.");
        setDeleting(false);
        onDeleted();
      } catch (error) {
        const message = mutationFailure(error, "delete this tier");
        setDeleteError(message);
        toast.error(message);
      }
    });
  }

  return (
    <div className="flex gap-2">
      <Button
        size="sm-touch"
        variant="outline"
        onClick={() => setRenaming(true)}
      >
        Rename
      </Button>
      <Button
        size="sm-touch"
        variant="outline"
        onClick={() => setDeleting(true)}
      >
        Delete
      </Button>

      <Dialog
        open={renaming}
        onOpenChange={(nextOpen) => {
          if (!renamePending) setRenaming(nextOpen);
        }}
        title="Rename tier"
        presentation="responsive-sheet"
      >
        <div className="space-y-3">
          <label className="block text-sm font-medium" htmlFor="tier-name">
            Tier name
          </label>
          <Input
            id="tier-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={renamePending}
          />
          {renameError && (
            <p role="alert" className="text-sm text-destructive">
              {renameError}
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button
              variant="ghost"
              size="touch"
              onClick={() => setRenaming(false)}
              disabled={renamePending}
            >
              Cancel
            </Button>
            <Button
              size="touch"
              onClick={() => void submitRename()}
              disabled={renamePending}
            >
              {renamePending ? "Saving..." : "Save tier"}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={deleting}
        onOpenChange={(nextOpen) => {
          if (!deletePending) setDeleting(nextOpen);
        }}
        title="Delete tier"
        presentation="responsive-sheet"
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {assignedCount > 0
              ? `${assignedCount} customer${assignedCount === 1 ? "" : "s"} still assigned. Remove them before deleting.`
              : `Delete ${tier.name}?`}
          </p>
          {deleteError && (
            <p role="alert" className="text-sm text-destructive">
              {deleteError}
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button
              variant="ghost"
              size="touch"
              onClick={() => setDeleting(false)}
              disabled={deletePending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="touch"
              onClick={() => void submitDelete()}
              disabled={deletePending || assignedCount > 0}
            >
              {deletePending ? "Deleting..." : "Delete tier"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function NewTierButton({ disabled }: { disabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const createTier = useCreateCarrierCustomerTier();
  const submission = useSubmissionGuard();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pending = submission.pending || createTier.isPending;

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setErrorMessage("Tier name is required.");
      return;
    }
    await submission.run(async () => {
      setErrorMessage(null);
      try {
        await createTier.mutateAsync({ name: trimmed });
        toast.success("Tier created.");
        setName("");
        setOpen(false);
      } catch (error) {
        const message = mutationFailure(error, "create this tier");
        setErrorMessage(message);
        toast.error(message);
      }
    });
  }

  return (
    <>
      <Button
        size="icon-sm-touch"
        variant="ghost"
        className="h-11 w-11 p-0 lg:h-8 lg:w-8"
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-label="New tier"
      >
        <Plus className="h-4 w-4" />
      </Button>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!pending) setOpen(nextOpen);
        }}
        title="New tier"
        presentation="responsive-sheet"
      >
        <div className="space-y-3">
          <label className="block text-sm font-medium" htmlFor="new-tier-name">
            Tier name
          </label>
          <Input
            id="new-tier-name"
            placeholder="e.g. Gold"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={pending}
          />
          {errorMessage && (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage}
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button
              variant="ghost"
              size="touch"
              onClick={() => setOpen(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              size="touch"
              onClick={() => void submit()}
              disabled={!name.trim() || pending}
            >
              {pending ? "Creating..." : "Create tier"}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
